import { useCallback } from 'react';

import { useCreateConsumerStudentFeeCheckout } from '@givebacks/api/store';
import {
  StudentFeesResponse,
  StudentResponse,
  useListConsumerStudentFees,
  useShowConsumerStudent,
} from '@givebacks/api/student';
import { useShopRoutes } from '@givebacks/shop';
import { getErrorMessage } from '@givebacks/utils';
import { Card, notifications } from 'ui';

import { StudentInfoCard } from './StudentInfoCard';
import { TotalDueCard } from './TotalDueCard';

type StudentInfoTotalDueCardProps = {
  studentId: string;
};

const getUnpaidFeeTotal = (data: StudentFeesResponse) => {
  return data?.student?.unpaid_fee_total;
};

const getStudent = (data: StudentResponse) => {
  return data.student;
};

const UnpaidCard = ({ studentId, totalDue }: { studentId: string; totalDue: number }) => {
  const { navigateToCheckout } = useShopRoutes();
  const { mutate: createConsumerStudentFeeCheckout, isPending: isCreatingStudentFeeCheckout } =
    useCreateConsumerStudentFeeCheckout();

  const onPayNowClick = useCallback(() => {
    // Create a consumer checkout cart with the results and
    // navigate to the checkout page
    createConsumerStudentFeeCheckout(
      { student_id: studentId },
      {
        onSuccess: (response) => {
          navigateToCheckout(response.checkout.uuid);
        },
        onError: (error) => {
          notifications.show({
            message: getErrorMessage(error, 'Error paying student fees'),
            variant: 'error',
          });
        },
      }
    );
  }, [createConsumerStudentFeeCheckout, navigateToCheckout, studentId]);

  return (
    // If any query is loading, don't show the pay now button
    <TotalDueCard
      radius="0"
      totalDue={totalDue}
      withPayNowButton={!!totalDue && totalDue > 0}
      buttonProps={{ onClick: onPayNowClick, loading: isCreatingStudentFeeCheckout }}
    />
  );
};

const StudentInfoTotalDueCard = ({ studentId }: StudentInfoTotalDueCardProps) => {
  const { data: unPaidTotal } = useListConsumerStudentFees({
    studentId: studentId,
    select: getUnpaidFeeTotal,
    queryOptions: {
      options: {
        include: 'unpaid_fee_total',
      },
    },
  });

  const { data: student } = useShowConsumerStudent({
    studentId: studentId,
    select: getStudent,
  });

  if (!student) return null;

  return (
    <Card bg="cobalt.0">
      <Card.Section inheritPadding p="sm">
        <StudentInfoCard student={student} />
      </Card.Section>
      <Card.Section>
        {unPaidTotal ? <UnpaidCard studentId={studentId} totalDue={unPaidTotal} /> : null}
      </Card.Section>
    </Card>
  );
};

export { StudentInfoTotalDueCard };
